import { Check, Gift, Truck } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { CtaButton } from "@/components/site/CtaButton";

const PACKS = [
  { id: "pack-1", tag: "Basic", jars: 1, supply: "30-day supply", per: 69, total: 69, was: 199, ship: "+ shipping" },
  { id: "pack-6", tag: "Most popular", jars: 6, supply: "180-day supply", per: 49, total: 294, was: 1194, bonus: true, ship: "Free USA shipping", hot: true },
  { id: "pack-3", tag: "Bundle", jars: 3, supply: "90-day supply", per: 59, total: 177, was: 597, bonus: true, ship: "Free USA shipping" },
];
const BONUSES = [["The Nitric Boost Blueprint", "Best times to take it, foods that pair with it and smoothie recipes. Listed at $73.50 value."], ["Marathon Man Stamina", "A guide to endurance, control and confidence. Listed at $73.50 value."]];

const Pack = ({ p, i }) => {
  const muted = p.hot ? "text-[#F2EDE4]/70" : "text-[#140A0C]/65";
  return (
    <Reveal delay={i * 0.1} className="h-full">
      <div data-testid={`nitric-${p.id}-card`}
        className={`flex h-full flex-col rounded-[2rem] p-7 transition-transform duration-500 hover:-translate-y-1 ${p.hot ? "bg-[#140A0C] text-[#F2EDE4] lg:-my-6 lg:py-12" : "bg-white text-[#140A0C]"}`}>
        <span className={`self-start rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] ${p.hot ? "bg-[#FF4D4D] text-[#140A0C]" : "bg-[#140A0C]/5 text-[#C62F2F]"}`}>{p.tag}</span>
        <p className="mt-6 font-display text-3xl font-bold">{p.jars} {p.jars === 1 ? "Jar" : "Jars"}</p>
        <p className={`text-sm ${muted}`}>{p.supply}</p>
        <p className="mt-6 flex items-baseline gap-1">
          <span className="font-display text-6xl font-extrabold tracking-tight" data-testid={`nitric-${p.id}-price`}>${p.per}</span>
          <span className={`text-sm ${muted}`}>/ jar</span>
        </p>
        <p className={`mt-1 text-sm ${muted}`}><span className="line-through">${p.was}</span> <span className={p.hot ? "text-[#F2EDE4]" : "text-[#140A0C]"}>${p.total} total</span></p>
        <ul className="mt-6 space-y-2 text-sm">
          <li className="flex items-center gap-2"><Truck className="h-4 w-4 text-[#FF4D4D]" /> {p.ship}</li>
          {p.bonus && <li className="flex items-center gap-2"><Gift className="h-4 w-4 text-[#FF4D4D]" /> 2 free bonuses</li>}
          <li className="flex items-center gap-2"><Check className="h-4 w-4 text-[#FF4D4D]" /> 60-day money-back guarantee</li>
        </ul>
        <div className="mt-auto pt-8">
          <CtaButton testId={`nitric-${p.id}-button`} placement={p.id} product="nitric-boost" variant="crimson" className={`w-full justify-between ${p.hot ? "" : "hover:!bg-[#140A0C] hover:!text-[#F2EDE4]"}`}>
            Buy {p.jars} {p.jars === 1 ? "jar" : "jars"}
          </CtaButton>
        </div>
      </div>
    </Reveal>
  );
};

export const NitricPricing = () => (
  <section id="offer" className="bg-[#F2EDE4] py-24 sm:py-32" data-testid="nitric-pricing-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="mb-16">
        <img src="/nitric-banner.webp" alt="Nitric Boost Ultra — 60-day money back guarantee, secure payment, one-time fee" data-testid="nitric-banner" className="w-full rounded-[1.5rem] shadow-xl" />
      </Reveal>
      <Reveal className="max-w-2xl">
        <Eyebrow className="!text-[#C62F2F]">Discounted packages</Eyebrow>
        <h2 className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-tight text-[#140A0C] sm:text-5xl">
          Claim your <span className="font-serif italic font-normal text-[#C62F2F]">discount.</span>
        </h2>
        <p className="mt-5 text-base text-[#140A0C]/75">Prices as listed on the official Nitric Boost site. Always confirm the final price at checkout.</p>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 items-stretch gap-5 lg:grid-cols-3 lg:py-6">
        {PACKS.map((p, i) => <Pack key={p.id} p={p} i={i} />)}
      </div>
      <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Reveal className="h-full">
          <div className="h-full rounded-[2rem] border border-[#140A0C]/10 p-6 sm:p-8" data-testid="nitric-bonuses">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#C62F2F]">With 3 & 6 jar packs</p>
            <p className="mt-2 font-display text-2xl font-bold text-[#140A0C]">2 free bonuses</p>
            <div className="mt-6 space-y-5">
              {BONUSES.map(([t, d], i) => (
                <div key={t} className="flex gap-4" data-testid={`nitric-bonus-${i + 1}`}>
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#140A0C] text-[#FF4D4D]"><Gift className="h-5 w-5" /></span>
                  <div>
                    <p className="font-display text-lg font-semibold text-[#140A0C]">{t}</p>
                    <p className="text-sm text-[#140A0C]/75">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="h-full">
          <div className="h-full rounded-[2rem] bg-[#140A0C] p-6 sm:p-8" data-testid="nitric-guarantee">
            <img src="/nitric-guarantee-badge.png" alt="60-day money back guarantee badge" className="h-24 w-auto" data-testid="nitric-guarantee-badge" />
            <p className="mt-4 font-display text-2xl font-bold text-[#F2EDE4]">60-day money-back guarantee</p>
            <p className="mt-3 text-sm leading-relaxed text-[#F2EDE4]/75">
              The maker states every order — 1, 3 or 6 containers — is covered for 60 days. If you're not happy, contact their support team for a full refund, no questions asked.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
